import React, { useState, useEffect, useCallback } from 'react';
import { partFilesAPI } from '../../services/api';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import { RefreshCw, Plus, Trash2, FileBox, Pencil, X } from 'lucide-react';
import '../Admin/AdminDashboard.css';

const STATUSES = ['draft', 'in_qualification', 'qualified', 'suspended', 'retired'];
const VERTICALS = ['maritime', 'superyacht', 'electronics', 'pharma', 'medtech', 'aerospace', 'other'];
const CRITICALITY = ['non_critical', 'functional', 'safety_related', 'regulated'];

const emptyForm = () => ({
  partId: '',
  partName: '',
  customerPartRef: '',
  oemReference: '',
  applicationDescription: '',
  criticality: 'functional',
  vertical: 'maritime',
  stExclusionCleared: false,
  status: 'draft',
});

const AdminPartFiles = () => {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partFilesAPI.getAll({ limit: 100, status: statusFilter || undefined });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load part files');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setShowForm(true); };

  const openEdit = (row) => {
    setEditingId(row._id);
    setForm({
      partId: row.partId || '',
      partName: row.partName || '',
      customerPartRef: row.customerPartRef || '',
      oemReference: row.oemReference || '',
      applicationDescription: row.applicationDescription || '',
      criticality: row.criticality || 'functional',
      vertical: row.vertical || 'maritime',
      stExclusionCleared: Boolean(row.stExclusionCleared),
      status: row.status || 'draft',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await partFilesAPI.update(editingId, form);
        toast.success('Part file updated');
      } else {
        await partFilesAPI.create(form);
        toast.success('Part file created');
      }
      setShowForm(false);
      load();
    } catch {
      toast.error('Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this part file?')) return;
    try {
      await partFilesAPI.delete(id);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <main className="admin-content">
        <div className="content-header">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FileBox size={28} color="#6366f1" /> Part Files
            </h1>
            <p style={{ color: '#a1a1aa' }}>
              Platform v0.1 part file register (geometry · material · process · acceptance).
              {total ? ` ${total} files.` : ' Empty — create after a screen converts.'}
            </p>
          </div>
          <div className="admin-header-right">
            <button type="button" onClick={openCreate} style={btn('#6366f1')}><Plus size={15} /> New part file</button>
            <button type="button" className="btn-secondary" onClick={load}><RefreshCw size={16} /></button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <Chip active={!statusFilter} onClick={() => setStatusFilter('')} label="All" color="#6366f1" />
          {STATUSES.map((s) => (
            <Chip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} label={s} color="#6366f1" />
          ))}
        </div>

        {showForm && (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: '#fff' }}>{editingId ? 'Edit part file' : 'New part file'}</h3>
              <button type="button" onClick={() => setShowForm(false)} style={iconBtn}><X size={16} /></button>
            </div>
            <div style={grid}>
              <label style={field}><span style={muted}>Part ID</span>
                <input value={form.partId} onChange={(e) => setForm((p) => ({ ...p, partId: e.target.value }))} placeholder="PF-2026-001" style={input} /></label>
              <label style={field}><span style={muted}>Part name</span>
                <input value={form.partName} onChange={(e) => setForm((p) => ({ ...p, partName: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>Customer part ref</span>
                <input value={form.customerPartRef} onChange={(e) => setForm((p) => ({ ...p, customerPartRef: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>OEM reference</span>
                <input value={form.oemReference} onChange={(e) => setForm((p) => ({ ...p, oemReference: e.target.value }))} style={input} /></label>
              <label style={field}><span style={muted}>Criticality</span>
                <select value={form.criticality} onChange={(e) => setForm((p) => ({ ...p, criticality: e.target.value }))} style={input}>
                  {CRITICALITY.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></label>
              <label style={field}><span style={muted}>Vertical</span>
                <select value={form.vertical} onChange={(e) => setForm((p) => ({ ...p, vertical: e.target.value }))} style={input}>
                  {VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
                </select></label>
              <label style={field}><span style={muted}>Status</span>
                <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} style={input}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select></label>
              <label style={{ ...field, gridColumn: '1 / -1' }}><span style={muted}>Application description</span>
                <textarea value={form.applicationDescription} onChange={(e) => setForm((p) => ({ ...p, applicationDescription: e.target.value }))} rows={2} style={input} /></label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#a1a1aa', fontSize: 13 }}>
                <input type="checkbox" checked={form.stExclusionCleared} onChange={(e) => setForm((p) => ({ ...p, stExclusionCleared: e.target.checked }))} />
                ST exclusion cleared
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="button" onClick={handleSave} style={btn('#6366f1')}>Save</button>
              <button type="button" onClick={() => setShowForm(false)} style={ghost}>Cancel</button>
            </div>
          </div>
        )}

        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr style={thead}>
                {['Part ID', 'Name', 'Vertical', 'Criticality', 'Status', 'ST cleared', 'Created', ''].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={emptyCell}>Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} style={emptyCell}>
                  No part files yet. Add qualified replacements after obsolescence screens convert.
                </td></tr>
              ) : items.map((row) => (
                <tr key={row._id} style={rowStyle}>
                  <td style={td}><strong>{row.partId || row._id.slice(-6)}</strong></td>
                  <td style={td}>{row.partName || '—'}</td>
                  <td style={td}>{row.vertical || '—'}</td>
                  <td style={td}>{row.criticality || '—'}</td>
                  <td style={td}><span style={pill('#6366f1')}>{row.status}</span></td>
                  <td style={td}>{row.stExclusionCleared ? 'Yes' : 'No'}</td>
                  <td style={td}>{fmt(row.createdAt)}</td>
                  <td style={td}>
                    <button type="button" onClick={() => openEdit(row)} style={iconBtn}><Pencil size={14} /></button>
                    <button type="button" onClick={() => handleDelete(row._id)} style={iconBtn}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

function Chip({ active, onClick, label, color }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '6px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
      border: active ? `2px solid ${color}` : '1px solid #27272a',
      background: active ? `${color}20` : '#18181b',
      color: active ? color : '#a1a1aa',
      fontWeight: active ? 600 : 400,
    }}>{label}</button>
  );
}

const btn = (c) => ({ background: c, border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 });
const ghost = { background: '#27272a', border: 'none', borderRadius: 6, padding: '8px 16px', color: '#a1a1aa', cursor: 'pointer' };
const iconBtn = { background: 'transparent', border: 'none', color: '#a1a1aa', cursor: 'pointer', padding: 6 };
const card = { background: '#18181b', borderRadius: 12, border: '1px solid #27272a', padding: 24, marginBottom: 24 };
const grid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const field = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 };
const muted = { color: '#71717a' };
const input = { background: '#111', border: '1px solid #27272a', borderRadius: 6, padding: '8px 12px', color: '#fff', fontSize: 13 };
const tableWrap = { background: '#111', borderRadius: 12, overflow: 'hidden', border: '1px solid #27272a' };
const table = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
const thead = { background: '#18181b', borderBottom: '1px solid #27272a' };
const th = { padding: '12px 16px', color: '#71717a', fontWeight: 600, textAlign: 'left' };
const td = { padding: '14px 16px', color: '#fff' };
const rowStyle = { borderBottom: '1px solid #1c1c1c' };
const emptyCell = { padding: 40, textAlign: 'center', color: '#71717a' };
const pill = (c) => ({ background: `${c}20`, color: c, borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 600 });

export default AdminPartFiles;
